import { Injectable, signal, computed } from '@angular/core';
import { WorkflowRole, ALL_WORKFLOW_ROLES, WORKFLOW_ROLE_LABELS } from '@core/models/workflow.model';

/**
 * Admin Role Switch Service
 *
 * Manages the demo-mode role override for Admin users.
 * When active, adds X-Demo-Role header to all API requests via interceptor.
 */
@Injectable({ providedIn: 'root' })
export class AdminRoleSwitchService {
  /** The currently selected demo role (null = use real role) */
  private readonly _activeRole = signal<WorkflowRole | null>(null);

  /** Public read-only signal */
  readonly activeRole = this._activeRole.asReadonly();

  /** Whether role switching is currently active */
  readonly isActive = computed(() => this._activeRole() !== null);

  /** Human-readable label for current role */
  readonly activeRoleLabel = computed(() => {
    const role = this._activeRole();
    return role ? WORKFLOW_ROLE_LABELS[role] : 'Real Role';
  });

  /** All available roles for the dropdown */
  readonly availableRoles = ALL_WORKFLOW_ROLES;

  /** Set the demo role override */
  switchRole(role: WorkflowRole | null): void {
    this._activeRole.set(role);
  }

  /** Clear the override (revert to real role) */
  clearOverride(): void {
    this._activeRole.set(null);
  }

  /** Get the header value to attach to requests (null if no override) */
  getDemoRoleHeader(): string | null {
    return this._activeRole();
  }
}
